import React, { useEffect, useMemo, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';

import Img from '../../../components/Img';
import { Datepicker, Select } from '../../../components/Form';
import { canPauseMatch, canRemoveMatch, canAdjustMatch, canStartMatch, courtGridDateFormat } from '../../../utils/courtSchedular';
import { adjustAllSelectedMatches, clearMatchSelection, deScheduleAllSelectedMatches, pauseAllSelectedMatches, revertScheduledMatches, selectAllMatches, setSelectedDrawWithMatch, setSelectedFacility, startAllSelectedMatches } from '../../../actions/court-scheduler-actions';

import styles from './courtSchedular.scss';
import Popup from '../../../components/Popup';
import AdjustMatchesModal from '../../../components/PopUps/AdjustMatchesModal';
import ConfirmPauseMatchPopup from '../../../components/PopUps/ConfirmPauseMatchPopup';
import ConfirmStartMatchPopup from '../../../components/PopUps/ConfirmStartMatchPopup';
import PrintMatchSheetModalV2 from '../PrintMatchSheetModalV2';
import * as drawEditActions from '../../../actions/draw-edit-actions';
import useHeaderDateFilter from '../SchedularTypeComponents/useHeaderDateFilter';
import { isPickleBallSportTypeEvent } from '../../../utils/events';
import PublishStatus from './PublishStatus';
import useCourtSchedularUnpublishedChangesGuard from './useCourtSchedularUnpublishedChangesGuard';

const MULTIPLE_MATCHES_FROM_SAME_DRAW_PRINT_LIMIT = 50;

const CourtGridHeader = (props) => {
    const {
        event,
        onClickSettings,
        onChangeDate,
        history,
    } = props;

    const dispatch = useDispatch();
    const [showConfirmRevertPopup, setShowConfirmRevertPopup] = useState(false);
    const [showConfirmPausePopup, setShowConfirmPausePopup] = useState(false);
    const [showConfirmStartPopup, setShowConfirmStartPopup] = useState(false);
    const [showConfirmPrintPopup, setShowConfirmPrintPopup] = useState(false);
    const [showAdjustModal, setShowAdjustModal] = useState(false);
    const [printDisabled, setPrintDisabled] = useState(false);
    const courtScheduler = useSelector(({ courtScheduler }) => courtScheduler);
    const {
        selectedDate,
        selectedDateCourtConfig,
        facilitiesAvailable,
        unPublishedMatchCount = 0,
        publishedDeletedDraftMatchCount = 0,
        matchesSaved = 0,
        drawsWithMatches = [],
        selectedDrawWithMatches = [],
        selectedFacilityId,
        selectedMatchIds = [],
        scheduledMatches = [],
        isPublishing = false,
        isReverting = false,
    } = courtScheduler;

    const { guardAction: guardUnpublishedChanges } = useCourtSchedularUnpublishedChangesGuard();

    const {
        calendarSelectedDate,
        calendarStartDate,
        calendarEndDate,
        onFacilitySelected,
        onDateChange,
        onGotoPreviousDayClick,
        onGotoNextDayClick,
    } = useHeaderDateFilter({
        event,
        onChangeDate,
        selectedDate,
        facilitiesAvailable,
        setSelectedFacility,
        guardUnpublishedChanges,
    });

    const areMatchesSelected = selectedMatchIds.length > 0;
    const shouldDisplaySelectAll = scheduledMatches.length > 0 && (selectedMatchIds.length !== scheduledMatches.length);
    const disableButtons = isPublishing || isReverting;
    const isPickleBallEvent = isPickleBallSportTypeEvent(event.sportTypeId);

    useEffect(() => {
        if (maximumPrintLimitReached()) {
            setPrintDisabled(true);
        } else {
            setPrintDisabled(false);
        }
    }, [selectedMatchIds]);

    const maximumPrintLimitReached = () => {
        const multipleMatchesSelected = selectedMatchIds.length > 1;

        if (multipleMatchesSelected) {
            // Step 1: Filter matches by the given IDs
            const filteredMatches = scheduledMatches.filter(match => selectedMatchIds.includes(match.id));
            // Step 2: Group filtered matches by drawId
            const groupedMatches = filteredMatches.reduce((groups, match) => {
                if (!groups[match.drawId]) {
                    groups[match.drawId] = [];
                }
                groups[match.drawId].push(match);
                return groups;
            }, {});
            // Step 3: Check if any group exceeds the supported limit for printing
            // eslint-disable-next-line no-unused-vars
            return Object.entries(groupedMatches).some(([drawId, matches]) => matches.length > MULTIPLE_MATCHES_FROM_SAME_DRAW_PRINT_LIMIT);
        }
        return false;
    };

    const printMatchSheet = (formValues) => {
        const multipleMatchesSelected = selectedMatchIds.length > 1;
        const eventId = event.id;
        const { scoreSheetType, pageFormat } = formValues;
        if (multipleMatchesSelected) {
            // Step 1: Filter matches by the given IDs
            const filteredMatches = scheduledMatches.filter(match => selectedMatchIds.includes(match.id));
            // Step 2: Group filtered matches by drawId
            const groupedMatches = filteredMatches.reduce((groups, match) => {
                if (!groups[match.drawId]) {
                    groups[match.drawId] = [];
                }
                groups[match.drawId].push(match);
                return groups;
            }, {});

            // Step 3: Open a new tab for each group using Object.entries
            Object.entries(groupedMatches).forEach(([drawId, matches]) => {
                const uniqueIds = new Set(matches.map(match => match.id));
                const matchesIds = Array.from(uniqueIds).join('_');

                // Call update draws/match API for saving isPrinted
                dispatch(drawEditActions.printMultipleMatchSheets({ eventId, drawId, matchesIds: uniqueIds }));

                const url = `/events/${eventId}/draws/${drawId}/print-matches?matchesArray=${matchesIds}&scoreSheetType=${scoreSheetType}&pageFormat=${pageFormat}`;
                window.open(url, '_blank');
            });
        } else {
            const matchId = selectedMatchIds[0];
            const match = scheduledMatches.find((m) => m.id === matchId);
            // Call update draws/match API for saving isPrinted
            if (formValues.markPrinted) {
                dispatch(drawEditActions.printMatchSheet({ reload: true, matchId }));
            }
            if (window) {
                window.open(`/events/${eventId}/draws/${match.drawId}/print-matches?matchId=${match.id}&scoringFormat=${match.scoringTypeId}&scoreSheetType=${scoreSheetType}&pageFormat=${pageFormat}`, '_blank');
            } else {
                history.push(`/events/${eventId}/draws/${match.drawId}/print-matches?matchId=${match.id}&scoringFormat=${match.scoringTypeId}&scoreSheetType=${scoreSheetType}&pageFormat=${pageFormat}`);
            }
        }
        setShowConfirmPrintPopup(false);
    };

    const onDrawWithMatchesSelected = (drawId) => {
        dispatch(setSelectedDrawWithMatch(drawId));
    };

    const hasUnpublishedMatches = unPublishedMatchCount > 0 ||
        publishedDeletedDraftMatchCount > 0 ||
        matchesSaved?.length > 0;

    const shouldDisplayPublishSection = selectedDateCourtConfig && selectedFacilityId;

    const onRevertClick = () => {
        setShowConfirmRevertPopup(true);
    };

    const onConfirmRevert = () => {
        setShowConfirmRevertPopup(false);
        dispatch(revertScheduledMatches());
    };

    const onCancelRevert = () => {
        setShowConfirmRevertPopup(false);
    };

    const onCancelPause = () => {
        setShowConfirmPausePopup(false);
    };

    const onConfirmPause = (pushBackMinutes = null) => {
        setShowConfirmPausePopup(false);
        dispatch(pauseAllSelectedMatches(pushBackMinutes));
    };

    const onCancelStart = () => {
        setShowConfirmStartPopup(false);
    };

    const onConfirmStart = () => {
        setShowConfirmStartPopup(false);
        dispatch(startAllSelectedMatches());
    };

    const onClearMatchSelections = () => {
        dispatch(clearMatchSelection());
    };

    const onSelectAllMatches = () => {
        dispatch(selectAllMatches());
    };

    const enableStartButton = useMemo(() => {
        const matchesAllowedToStart = scheduledMatches
            .filter((match) => selectedMatchIds.includes(match.id))
            .filter((match) => canStartMatch(match, scheduledMatches));

        return matchesAllowedToStart.length === selectedMatchIds.length;
    }, [selectedMatchIds, scheduledMatches]);

    const enablePauseButton = useMemo(() => {
        const matchesAllowedToStart = scheduledMatches
            .filter((match) => selectedMatchIds.includes(match.id))
            .filter(canPauseMatch);

        return matchesAllowedToStart.length === selectedMatchIds.length;
    }, [selectedMatchIds, scheduledMatches]);

    const enableRemoveButton = useMemo(() => {
        const matchesAllowedToStart = scheduledMatches
            .filter((match) => selectedMatchIds.includes(match.id))
            .filter(canRemoveMatch);

        return matchesAllowedToStart.length === selectedMatchIds.length;
    }, [selectedMatchIds, scheduledMatches]);

    const enableAdjustButton = useMemo(() => {
        const matchesAllowedToAdjust = scheduledMatches
            .filter((match) => selectedMatchIds.includes(match.id))
            .filter(canAdjustMatch);

        return matchesAllowedToAdjust.length === selectedMatchIds.length;
    }, [selectedMatchIds, scheduledMatches]);

    const onRemoveClick = () => {
        dispatch(deScheduleAllSelectedMatches());
    };

    const onStartClick = () => {
        setShowConfirmStartPopup(true);
    };

    const onPauseClick = () => {
        setShowConfirmPausePopup(true);
    };

    const onPrintClick = () => {
        setShowConfirmPrintPopup(true);
    };

    const onAdjustClick = () => {
        setShowAdjustModal(true);
    };

    const onCancelAdjust = () => {
        setShowAdjustModal(false);
    };

    const onConfirmAdjust = (adjustmentMinutes) => {
        setShowAdjustModal(false);
        dispatch(adjustAllSelectedMatches(adjustmentMinutes));
    };

    return (
        <>
            <div className={styles.courtGridHeader}>
                {!areMatchesSelected && (
                    <>
                        <Select
                            attributeToShow="name"
                            attributeToSave="id"
                            themeV3
                            emptyOption="All Draws"
                            options={drawsWithMatches}
                            formGroupCssClass={styles.optionSelect}
                            controlled
                            value={selectedDrawWithMatches}
                            onChange={onDrawWithMatchesSelected}
                            disabled={drawsWithMatches.length < 2}
                            data-testid="court-scheduler.header.draws-select"
                        />
                        <Select
                            attributeToShow="name"
                            attributeToSave="id"
                            themeV3
                            emptyOption="Select Facility"
                            options={facilitiesAvailable}
                            formGroupCssClass={styles.optionSelect}
                            controlled
                            hiddenDefault={facilitiesAvailable.length > 0}
                            value={selectedFacilityId}
                            onChange={onFacilitySelected}
                            disabled={facilitiesAvailable.length < 2}
                            data-testid="court-scheduler.header.facility-select"
                        />
                        <div className={styles.divider} />
                        <button
                            className={`btn ${styles.dateSlider}`}
                            type="button"
                            onClick={onGotoPreviousDayClick}
                            disabled={calendarSelectedDate.isSame(calendarStartDate)}
                            data-testid="court-scheduler.header.prev-day-btn"
                        >
                            <Img src="/img/v3/icons/icon-24px-arrow-left.svg" />
                        </button>
                        <Datepicker
                            className={`${styles.optionSelect} ${styles.dateSelect}`}
                            selected={calendarSelectedDate}
                            format={courtGridDateFormat}
                            minDate={calendarStartDate}
                            maxDate={calendarEndDate}
                            themeV3
                            onChange={onDateChange}
                        />
                        <button
                            className={`btn ${styles.dateSlider}`}
                            type="button"
                            onClick={onGotoNextDayClick}
                            disabled={calendarSelectedDate.isSame(calendarEndDate)}
                            data-testid="court-scheduler.header.next-day-btn"
                        >
                            <Img src="/img/v3/icons/icon-24px-arrow-right.svg" />
                        </button>
                    </>
                )}
                {areMatchesSelected && (
                    <>
                        <button
                            className={`btn btn-default ${styles.matchAction} ${!enableStartButton && styles.disabled}`}
                            type="button"
                            onClick={onStartClick}
                            disabled={!enableStartButton}
                            data-testid="court-scheduler.header.start-btn"
                        >
                            Start
                        </button>
                        <button
                            className={`btn btn-default ${styles.matchAction} ${!enablePauseButton && styles.disabled}`}
                            type="button"
                            onClick={onPauseClick}
                            disabled={!enablePauseButton}
                            data-testid="court-scheduler.header.pause-btn"
                        >
                            Pause
                        </button>
                        <button
                            className={`btn btn-default ${styles.matchAction} ${!enableRemoveButton && styles.disabled}`}
                            type="button"
                            onClick={onRemoveClick}
                            disabled={!enableRemoveButton}
                            data-testid="court-scheduler.header.remove-btn"
                        >
                            Remove
                        </button>
                        <button
                            className={`btn btn-default ${styles.matchAction} ${!enableAdjustButton && styles.disabled}`}
                            type="button"
                            onClick={onAdjustClick}
                            disabled={!enableAdjustButton}
                            data-testid="court-scheduler.header.adjust-btn"
                        >
                            Adjust
                        </button>
                        {isPickleBallEvent &&
                            (
                                <>
                                    <button
                                        className={`btn btn-default ${styles.matchAction} ${printDisabled && styles.disabled}`}
                                        type="button"
                                        onClick={onPrintClick}
                                        disabled={printDisabled}
                                        data-tip={`Maximum of ${MULTIPLE_MATCHES_FROM_SAME_DRAW_PRINT_LIMIT} matches can be printed at the same time for a single draw`}
                                        data-testid="court-scheduler.header.print-btn"
                                    >

                                        Print
                                    </button>
                                    {printDisabled && <ReactTooltip place="bottom" type="dark" effect="solid" />}
                                </>
                            )}
                    </>
                )}
                <div className="flex-1" />
                {areMatchesSelected && (
                    <button
                        className={`btn btn-flex ${styles.settings} p0`}
                        type="button"
                        onClick={onClearMatchSelections}
                        data-testid="court-scheduler.header.clear-selections-btn"
                    >
                        <Img className="mr4 mb2" src="/img/v3/icons/icon-14px-cancel-blue.svg" /> Clear selections
                    </button>
                )}
                {shouldDisplaySelectAll && (
                    <button
                        className={`btn btn-flex ${styles.settings} p0`}
                        type="button"
                        onClick={onSelectAllMatches}
                        data-testid="court-scheduler.header.select-all-btn"
                    >
                        <Img className="mr4 mb2" src="/img/v3/icons/icon-14px-checkbox-blue.svg" /> Select all
                    </button>
                )}
                {!areMatchesSelected && (
                    <>
                        {hasUnpublishedMatches && (
                            <button
                                className={`btn btn-flex ${styles.settings} p0`}
                                type="button"
                                disabled={disableButtons}
                                onClick={onRevertClick}
                                data-testid="court-scheduler.header.revert-btn"
                            >
                                <Img className="mr4 mb2" src="/img/v3/icons/icon-24px-revert-blue.svg" /> Revert
                            </button>
                        )}
                        <button
                            className={`btn btn-flex ${styles.settings} p0`}
                            type="button"
                            onClick={onClickSettings}
                            data-testid="court-scheduler.header.settings-btn"
                        >
                            <Img className="mr4 mb2" src="/img/v3/icons/icon-24px-settings-blue.svg" /> Settings
                        </button>
                    </>
                )}
                {shouldDisplayPublishSection && (
                    <div className={styles.dividerNew} />
                )}
                {shouldDisplayPublishSection && (
                    <PublishStatus event={event} />
                )}
            </div>
            {showConfirmRevertPopup && (
                <Popup
                    className="popup-sm"
                    popUpClassName={styles.confirmCourtSelectionPopup}
                    onCloseClick={onCancelRevert}
                    closeOnClickOutside={false}
                    config={{
                        fullScreen: false,
                        noPadding: true,
                        scrollable: false,
                        showCloseIcon: false,
                    }}
                >
                    <p className={styles.description} data-testid="modal.court-scheduler.revert.message">
                        Are you sure? This will set all matches to last published state.
                    </p>
                    <div className="d-flex mt16">
                        <button
                            className="btn btn-default flex-1 mr8"
                            type="button"
                            onClick={onCancelRevert}
                            data-testid="modal.court-scheduler.revert.cancel-btn"
                        >
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary flex-1"
                            type="button"
                            onClick={onConfirmRevert}
                            data-testid="modal.court-scheduler.revert.ok-btn"
                        >
                            OK
                        </button>
                    </div>
                </Popup>
            )}
            {showConfirmPausePopup && (
                <ConfirmPauseMatchPopup
                    onCloseClick={onCancelPause}
                    onConfirmPause={onConfirmPause}
                />
            )}
            {showConfirmStartPopup && (
                <ConfirmStartMatchPopup
                    onCloseClick={onCancelStart}
                    onConfirmStart={onConfirmStart}
                />
            )}
            {showConfirmPrintPopup && (
                <PrintMatchSheetModalV2
                    onCloseClick={() => setShowConfirmPrintPopup(false)}
                    onConfirmPrint={printMatchSheet}
                    showMarkPrinted={selectedMatchIds.length < 2}
                />
            )}
            {showAdjustModal && (
                <AdjustMatchesModal
                    onCloseClick={onCancelAdjust}
                    onConfirmAdjust={onConfirmAdjust}
                />
            )}
        </>
    );
};

CourtGridHeader.propTypes = {
    event: PropTypes.objectOf(any).isRequired,
    onClickSettings: PropTypes.func.isRequired,
    onChangeDate: PropTypes.func.isRequired,
    history: PropTypes.objectOf(any).isRequired,
};

CourtGridHeader.defaultProps = {};

export default CourtGridHeader;
